/**
 * safeConfirm — drop-in replacement for window.confirm that keeps working
 * inside sandboxed iframes (e.g. the hosted preview), where native
 * confirm()/prompt() dialogs are silently suppressed and return false.
 *
 * Strategy: call window.confirm first. If it returns false in under ~30ms
 * we assume the dialog was suppressed (a human can't read + click Cancel
 * that fast) and fall back to an in-app sonner toast with Confirm/Cancel
 * action buttons.
 *
 * Usage:  if (!(await safeConfirm('Delete this item?'))) return;
 */
import React from 'react';
import { toast } from 'sonner';

export function confirmToast(message, { confirmLabel = 'Confirm', danger = true } = {}) {
  return new Promise((resolve) => {
    let settled = false;
    const done = (v, id) => {
      if (settled) return;
      settled = true;
      toast.dismiss(id);
      resolve(v);
    };
    const id = toast(
      <div className="text-sm" data-testid="confirm-toast">
        <div className="font-medium mb-2">{message}</div>
        <div className="flex gap-2 justify-end">
          <button
            className="px-3 py-1.5 rounded-md border border-gray-300 text-xs"
            onClick={() => done(false, id)}
            data-testid="confirm-toast-cancel"
          >
            Cancel
          </button>
          <button
            className={`px-3 py-1.5 rounded-md text-xs text-white ${danger ? 'bg-[#B83A3A]' : 'bg-[#003D80]'}`}
            onClick={() => done(true, id)}
            data-testid="confirm-toast-confirm"
          >
            {confirmLabel}
          </button>
        </div>
      </div>,
      { duration: 15000, onDismiss: () => done(false, id), onAutoClose: () => done(false, id) },
    );
  });
}

export async function safeConfirm(message, opts = {}) {
  // Always use the in-app confirm toast: native confirm() is suppressed in
  // sandboxed iframes, and detecting suppression via timing is brittle under
  // CPU throttle. The toast works identically in every environment.
  return confirmToast(message, opts);
}
